import {useMemo} from 'react';
import {useSelector, shallowEqual} from 'react-redux';
import {useParams} from 'react-router-dom';
import type {DocumentStatuses, DocumentWithStatuses} from '../utils/getDocumentStatuses';
import {
    getDocumentWithStatuses,
    getAreAllDocumentsConfirmed,
    getNextPendingDocumentId,
    getFirstDocumentId,
    getIsRequiredActionsDone,
    getHasOnlyOptionalActions
} from '../utils/getDocumentStatuses';
import {
    selectVisibleDocuments,
    selectApprovals,
    selectFields
} from '../signerUiSlice';
import {getFieldsForDocument} from '../utils/selectors';

interface UseDocumentStatusesResult extends DocumentStatuses {
    documentId: string | null;
    isAllDocumentsConfirmed: boolean;
    nextPendingDocumentId: string | null;
    isRequiredActionsDone: boolean;
    hasOnlyOptionalActions: boolean;
}

const DEFAULT_STATUSES: DocumentStatuses = {
    isConfirmed: false,
    isReadyToConfirm: false,
    isReview: false,
    isPending: false,
    isOngoing: false,
    isToDo: false,
    isLast: false,
    isAcceptOnly: false,
    isEligibleToSign: false
};

/**
 * Returns all documents enriched with computed status flags.
 * This is the base hook that computes document statuses from Redux state.
 */
export function useDocumentsWithStatuses(): DocumentWithStatuses[] {
    const allDocuments = useSelector(selectVisibleDocuments) || [];
    const approvalsMap = useSelector(selectApprovals, shallowEqual) || {};
    const fieldsMap = useSelector(selectFields, shallowEqual) || {};

    return useMemo(
        () => allDocuments.map((doc) => getDocumentWithStatuses({
            document: doc,
            approvals: approvalsMap[doc.id] || [],
            fields: getFieldsForDocument(doc.id, approvalsMap, fieldsMap),
            allDocuments,
            approvalsMap,
            fieldsMap
        })),
        [allDocuments, approvalsMap, fieldsMap]
    );
}

/**
 * Returns all document statuses and derived signing state for a specific document.
 * Uses URL params for documentId if not explicitly provided.
 *
 * @param providedDocumentId - Optional document ID. Falls back to URL param if not provided.
 */
export function useDocumentStatuses(providedDocumentId?: string | null): UseDocumentStatusesResult {
    const {documentId: urlDocumentId} = useParams<{documentId: string}>();
    const documentId = providedDocumentId ?? urlDocumentId ?? null;

    const approvalsMap = useSelector(selectApprovals, shallowEqual);
    const fieldsMap = useSelector(selectFields, shallowEqual);
    const documentsWithStatuses = useDocumentsWithStatuses();

    return useMemo(() => {
        const currentDocument = documentsWithStatuses.find((doc) => doc.id === documentId);
        const approvals = approvalsMap[documentId ?? ''] || [];
        const fields = getFieldsForDocument(documentId, approvalsMap, fieldsMap);

        const statuses = currentDocument
            ? {
                isConfirmed: currentDocument.isConfirmed,
                isReadyToConfirm: currentDocument.isReadyToConfirm,
                isReview: currentDocument.isReview,
                isPending: currentDocument.isPending,
                isOngoing: currentDocument.isOngoing,
                isToDo: currentDocument.isToDo,
                isLast: currentDocument.isLast,
                isAcceptOnly: currentDocument.isAcceptOnly,
                isEligibleToSign: currentDocument.isEligibleToSign
            }
            : DEFAULT_STATUSES;

        return {
            documentId,
            ...statuses,
            isAllDocumentsConfirmed: getAreAllDocumentsConfirmed(documentsWithStatuses),
            nextPendingDocumentId: getNextPendingDocumentId(documentsWithStatuses, documentId),
            isRequiredActionsDone: getIsRequiredActionsDone(approvals, fields),
            hasOnlyOptionalActions: getHasOnlyOptionalActions(approvals, fields)
        };
    }, [documentId, documentsWithStatuses, approvalsMap, fieldsMap]);
}

export {
    getAreAllDocumentsConfirmed, getNextPendingDocumentId, getFirstDocumentId
};
export type {DocumentStatuses, DocumentWithStatuses};
